import { mapGetters } from 'vuex';
import toast from '@/utils/toast';
import ModalAttendanceSchedules from './components/ModalAttendanceSchedules.vue';
import { formatBatchLabel } from '@/utils/batchIdentity';

export default {
  name: 'AttendanceReport',
  components: {
    ModalAttendanceSchedules
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      pageSizeOptions: [10, 25, 50, 100],
      filters: {
        search: '',
        date: null,
        batch: null
      },
      batchOptions: [
        { value: 1, label: 'Batch 1' },
        { value: 2, label: 'Batch 2' },
        { value: 3, label: 'Batch 3' },
        { value: 4, label: 'Batch 4' },
        { value: 5, label: 'Batch 5' },
        { value: 6, label: 'Batch 6' },
        { value: 7, label: 'Batch 7' },
        { value: 8, label: 'Batch 8' },
        { value: 9, label: 'Batch 9' },
        { value: 10, label: 'Batch 10' },
        { value: 11, label: 'Batch 11' },
        { value: 12, label: 'Batch 12' },
        { value: 13, label: 'Batch 13' },
        { value: 14, label: 'Batch 14' },
        { value: 15, label: 'Batch 15' }
      ],
      statusOptions: [
        { value: 'completed', label: 'Completed' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'upcoming', label: 'Upcoming' },
        { value: 'scheduled', label: 'Scheduled' },
        { value: 'not_started', label: 'Not Started' }
      ],
      downloadingReport: false,
      downloadingAll: false,
      selectedReportId: null,
      selectedTrainingEventId: null
    };
  },
  
  computed: {
    ...mapGetters('attendanceReport', ['reports', 'pagination', 'loading', 'error']),
    
    isLoading() {
      return this.loading;
    },
    
    paginatedReports() {
      return this.reports || [];
    },
    
    totalItems() {
      return this.pagination?.total || 0;
    },
    
    totalPages() {
      return this.pagination?.totalPage || 0;
    },
    
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      
      if (total <= 7) {
        // Show all pages if total is 7 or less
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        // Always show first page
        pages.push(1);
        
        // Show ellipsis if current page is far from start
        if (current > 4) {
          pages.push('...');
        }
        
        // Calculate the range of pages to show around current page
        const start = Math.max(2, current - 2);
        const end = Math.min(total - 1, current + 2);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        // Show ellipsis if current page is far from end
        if (current < total - 3) {
          pages.push('...');
        }
        
        // Always show last page
        pages.push(total);
      }
      
      return pages;
    }
  },
  
  watch: {
    'filters.search'() {
      this.currentPage = 1;
      this.debouncedFetchReports();
    },
    'filters.date'() {
      this.currentPage = 1;
      this.fetchReports();
    },
    'filters.batch'() {
      this.currentPage = 1;
      this.fetchReports();
    }
  },
  
  created() {
    this.debouncedFetchReports = this.debounce(this.fetchReports, 500);
    this.fetchReports();
  },
  
  methods: {
    formatBatchLabel,
    async fetchReports() {
      const params = {
        page: this.currentPage,
        limit: this.perPage,
        search: this.filters.search || undefined,
        date: this.filters.date || undefined,
        batch: this.filters.batch || undefined
      };
      
      try {
        await this.$store.dispatch('attendanceReport/fetchReports', params);
      } catch (error) {
        console.error('Error fetching reports:', error);
        toast({ type: 'error', title: 'Error', message: 'Laporan absensi belum berhasil dimuat' });
      }
    },
    
    goToPage(page) {
      if (page === '...' || page === this.currentPage || page < 1 || page > this.totalPages) {
        return;
      }
      this.currentPage = page;
      this.fetchReports();
      this.scrollToTop();
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.fetchReports();
        this.scrollToTop();
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.fetchReports();
        this.scrollToTop();
      }
    },

    handlePageSizeChange() {
      this.currentPage = 1;
      this.fetchReports();
      this.scrollToTop();
    },
    
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    
    clearFilters() {
      this.filters.search = '';
      this.filters.date = null;
      this.filters.batch = null;
      this.currentPage = 1;
      this.fetchReports();
    },
    
    getStatusClass(status) {
      const statusMap = {
        'completed': 'badge-success',
        'in_progress': 'badge-info',
        'upcoming': 'badge-primary',
        'scheduled': 'badge-warning',
        'not_started': 'badge-secondary'
      };
      return statusMap[status] || 'badge-secondary';
    },
    
    getStatusLabel(status) {
      const statusMap = {
        'completed': 'Completed',
        'in_progress': 'In Progress',
        'upcoming': 'Upcoming',
        'scheduled': 'Scheduled',
        'not_started': 'Not Started'
      };
      return statusMap[status] || status;
    },
    
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}-${month}-${year}`;
    },
    
    async viewReport(report) {
      this.selectedReportId = report.id;
      this.selectedTrainingEventId = report.id;
      this.$bvModal.show('modal-attendance-schedules');
    },
    
    closeModal() {
      this.selectedReportId = null;
      this.selectedTrainingEventId = null;
    },
    
    async downloadReport(report) {
      this.downloadingReport = report.id;
      
      try {
        const response = await this.$store.dispatch('attendanceReport/downloadReport', report.id);
        
        // Create a blob from the response
        const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `attendance-report-${report.training_title}-${report.batch}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast({ type: 'success', title: 'Success', message: 'Report downloaded successfully' });
      } catch (error) {
        console.error('Error downloading report:', error);
        toast({ type: 'error', title: 'Error', message: 'Laporan belum berhasil diunduh' });
      } finally {
        this.downloadingReport = null;
      }
    },
    
    async downloadAllReports() {
      this.downloadingAll = true;
      
      try {
        const params = {
          search: this.filters.search || undefined,
          status: this.filters.status || undefined
        };
        
        const response = await this.$store.dispatch('attendanceReport/downloadAllReports', params);
        
        // Create a blob from the response
        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `attendance-reports-${new Date().getTime()}.zip`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast({ type: 'success', title: 'Success', message: 'Semua laporan berhasil diunduh' });
      } catch (error) {
        console.error('Error downloading all reports:', error);
        toast({ type: 'error', title: 'Error', message: 'Semua laporan belum berhasil diunduh' });
      } finally {
        this.downloadingAll = false;
      }
    },
    
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  }
};
